import { useEffect, useMemo } from "react";
import { Input, Tag, Text } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { EnumProductType } from "gql/generated";
import {
  getMultilingualString,
  getResolvedProductSellingConfig,
} from "shared/utils";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { CollapsableStep } from "widgets/booker/shared";

const STORAGE_KEY_PREFIX = "wa-event-guests-";

type GuestSlot = { name: string; email: string; tierLabel: string };

export const EventGuestDetailsStep = () => {
  const { t } = useTranslation("booker", { keyPrefix: "EVENT_GUEST_DETAILS" });
  const { product, booker, language } = useBookerContext();
  const { cart, eventGuestDetails, updateEventGuestDetails } = useCartContext();

  const guestNamesEnabled = booker?.customerFields?.guestNamesEnabled ?? false;
  const guestEmailEnabled = booker?.customerFields?.guestEmailEnabled ?? false;
  const guestInfoRequired = booker?.customerFields?.guestInfoRequired ?? false;

  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );

  // Build a flat guest list with tier labels derived from the cart's
  // guestsPerTier (packages) or plain quantity (simple tickets).
  const guestSlots = useMemo<GuestSlot[]>(() => {
    if (!product || product.type !== EnumProductType.Event) return [];

    const productId = product._id;
    const eventRows = cart.filter(
      item => item.productId.split("::")[0] === productId,
    );

    const tierNameById = Object.fromEntries(
      sellingConfig.options.map(opt => [
        opt._id,
        getMultilingualString(opt.name, language),
      ]),
    );

    const slots: GuestSlot[] = [];
    for (const row of eventRows) {
      const tiers = row.guestsPerTier ?? [];
      if (tiers.length > 0) {
        for (const tier of tiers) {
          const label = tierNameById[tier.tierId ?? ""] ?? "";
          for (let i = 0; i < (tier.count ?? 0); i++) {
            slots.push({
              name: eventGuestDetails[slots.length]?.name ?? "",
              email: eventGuestDetails[slots.length]?.email ?? "",
              tierLabel: label,
            });
          }
        }
      } else {
        for (let i = 0; i < (row.quantity ?? 0); i++) {
          slots.push({
            name: eventGuestDetails[slots.length]?.name ?? "",
            email: eventGuestDetails[slots.length]?.email ?? "",
            tierLabel: "",
          });
        }
      }
    }
    return slots;
  }, [cart, product, sellingConfig.options, language, eventGuestDetails]);

  const totalTickets = guestSlots.length;

  // Sync the details array length to the ticket count.
  useEffect(() => {
    if (totalTickets <= 0) {
      if (eventGuestDetails.length > 0) updateEventGuestDetails([]);
      return;
    }
    if (eventGuestDetails.length !== totalTickets) {
      const next = Array.from({ length: totalTickets }, (_, i) => ({
        name: eventGuestDetails[i]?.name ?? "",
        email: eventGuestDetails[i]?.email ?? "",
      }));
      updateEventGuestDetails(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalTickets]);

  // localStorage autosave / clear.
  const storageKey = `${STORAGE_KEY_PREFIX}${booker?._id ?? ""}`;
  useEffect(() => {
    try {
      if (eventGuestDetails.length === 0) {
        localStorage.removeItem(storageKey);
      } else {
        localStorage.setItem(storageKey, JSON.stringify(eventGuestDetails));
      }
    } catch {
      // localStorage full or unavailable
    }
  }, [eventGuestDetails, storageKey]);

  // Hydrate from localStorage on mount.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { name: string; email: string }[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          updateEventGuestDetails(parsed);
        }
      }
    } catch {
      // corrupt data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  if (
    !guestNamesEnabled ||
    !product ||
    product.type !== EnumProductType.Event ||
    totalTickets <= 0
  ) {
    return null;
  }

  const updateGuest = (
    index: number,
    field: "name" | "email",
    value: string,
  ) => {
    const next = [...eventGuestDetails];
    next[index] = { ...next[index], [field]: value };
    updateEventGuestDetails(next);
  };

  const filledCount = eventGuestDetails.filter(g => g.name.trim()).length;
  const isComplete = guestInfoRequired
    ? eventGuestDetails.every(
        g =>
          g.name.trim() &&
          (!guestEmailEnabled || g.email.trim()),
      )
    : true;

  return (
    <CollapsableStep
      title={t("TITLE")}
      complete={isComplete}
      stepId="guest-details"
    >
      <Text color="text.mid">
        {t("SUBTITLE", { count: totalTickets, filled: filledCount })}
      </Text>
      <Flex flexDirection="column" gap="200">
        {guestSlots.map((slot, index) => (
          <Flex
            key={index}
            flexDirection="column"
            gap="100"
            p="200"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.light"
            borderRadius="md"
          >
            {slot.tierLabel && (
              <Tag visual="highlight" mode="light" alignSelf="flex-start">
                {slot.tierLabel}
              </Tag>
            )}
            <Flex gap="150" alignItems="flex-end">
              <Input
                label={`${t("GUEST_NAME")} ${index + 1}`}
                value={slot.name}
                onChange={e => updateGuest(index, "name", e.target.value)}
              />
              {guestEmailEnabled && (
                <Input
                  label={t("GUEST_EMAIL")}
                  value={slot.email}
                  onChange={e => updateGuest(index, "email", e.target.value)}
                />
              )}
            </Flex>
          </Flex>
        ))}
      </Flex>
    </CollapsableStep>
  );
};
